import { getIfValidStringifiedObject } from '@fabric-ui/utils';
import { FilterFn, Row, Table } from '@tanstack/react-table';
import { DataRecord, FormatterType } from './table.types';

const stringOperators = {
  contains: (str: string, value: string) => str.toLowerCase().includes(value),
  notContains: (str: string, value: string) => !str.toLowerCase().includes(value),
  starts: (str: string, value: string) => str.toLowerCase().startsWith(value),
  ends: (str: string, value: string) => str.toLowerCase().endsWith(value),
  equals: (str: string, value: string) => str.toLowerCase() === value,
  notEqual: (str: string, value: string) => str.toLowerCase() !== value,
};

const numberOperators = {
  equals: (rowValue: number, value: number) => rowValue === value,
  notEqual: (rowValue: number, value: number) => rowValue !== value,
  gt: (rowValue: number, value: number) => rowValue > value,
  gte: (rowValue: number, value: number) => rowValue >= value,
  lt: (rowValue: number, value: number) => rowValue < value,
  lte: (rowValue: number, value: number) => rowValue <= value,
};

export const globalFilterFn: FilterFn<DataRecord> = (row, columnId, filterValue) => {
  if (!filterValue || filterValue.length === 0) {
    return true;
  }

  const cell = row.getAllCells().find(c => c.column.id === columnId);
  if (!cell) {
    return true;
  }

  const rowValue = row.getValue(columnId);
  const columnType = cell.column.columnDef.meta?.type;

  const conditionFilter = getIfValidStringifiedObject(filterValue);

  if (conditionFilter) {
    const rootOperator = Object.keys(conditionFilter)[0];
    const { value, operator } = conditionFilter[rootOperator][0];
    if (typeof value === 'string' && operator in stringOperators) {
      const lowerCasedValue = value?.toLowerCase();
      const strRowValue = `${rowValue}`;
      return stringOperators[operator](strRowValue, lowerCasedValue);
    }
    if (typeof value === 'number' && operator in numberOperators) {
      return numberOperators[operator](rowValue, value);
    }
    if (operator === 'empty') {
      return !rowValue;
    }
    if (operator === 'notEmpty') {
      return Boolean(rowValue);
    }
    return false;
  }

  if (Array.isArray(filterValue)) {
    if (Array.isArray(rowValue)) {
      return rowValue.some(v => filterValue.includes(v));
    }
    if (columnType === FormatterType.boolean) {
      return filterValue.includes(rowValue);
    }
    return filterValue.includes(rowValue);
  }

  return true;
};

export const flatFacetedUniqueValues =
  () =>
  (table: Table<any>, columnId: string): (() => Map<any, number>) =>
  () => {
    const rowModel = table.getPreFilteredRowModel();
    if (!rowModel) {
      return new Map();
    }

    return rowModel.rows
      .flatMap((row: Row<any>) => {
        const value = row.getValue(columnId);
        return Array.isArray(value) ? value : [value];
      })
      .reduce((acc, item) => acc.set(item, (acc.get(item) || 0) + 1), new Map<any, number>());
  };
